import { makeQueryOptions } from '@mint-lib/api-zod-support/react-query';

import { defaultApi, defaultClient, type BaseQuery } from '@mint-sdk/api-core';

import {
  UTMTableResponseDeprecatedSchema,
  UTMTableResponseSchema,
} from '../shared/schemas/taxonomy.js';
import {
  getDynamicFieldsResponseSchema,
  getHasPopulatesRulesQuerySchema,
  getUTMConfigQueryDeprecatedSchema,
  getUTMConfigQuerySchema,
  getUTMOptionsQuerySchema,
  getUTMOptionsWithSearchQuerySchema,
  getUTMTableQuerySchema,
  hasPopulatesRulesResponseSchema,
  UTMConfigsResponseDeprecatedSchema,
  UTMConfigsResponseSchema,
  UTMOptionsResponseSchema,
  UTMOptionsWithSearchResponseSchema,
} from '../shared/schemas/utm.js';

// Utm config
export const getUTMConfigQueryOptions = defaultClient.query({
  url: `ga4-service/v3/utm-configs/` as const,
  method: 'GET',
  snakeCaseQueryParams: true,
  querySchema: getUTMConfigQuerySchema,
  responseSchema: UTMConfigsResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const getUTMConfigDeprecated = defaultApi.declareEndpoint({
  url: `ga4-service/utm-configs/` as const,
  method: 'GET',
  responseSchema: UTMConfigsResponseDeprecatedSchema,
  querySchema: getUTMConfigQueryDeprecatedSchema,
  snakeCaseQueryParams: true,
});

export const getUTMConfigQueryOptionsDeprecated = (baseQuery?: BaseQuery) => {
  return makeQueryOptions(
    getUTMConfigDeprecated,
    {
      processResponse(data) {
        return data;
      },
    },
    // `BaseQuery` is the loosely-typed default from `makeQueryOptions` (Result = unknown).
    // Cast to `any` so the concrete endpoint's narrowed Result doesn't reject the spread.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see note above
    { ...baseQuery } as any,
  );
};

// Table data
export const getUTMTable = defaultApi.declareEndpoint({
  url: `ga4-service/v3/utm-configs/$utmConfigId/table/` as const,
  method: 'GET',
  responseSchema: UTMTableResponseSchema,
  querySchema: getUTMTableQuerySchema,
  snakeCaseQueryParams: true,
});

export const getUTMTableDeprecated = defaultApi.declareEndpoint({
  url: `ga4-service/v2/utm-configs/$utmConfigId/table` as const,
  method: 'GET',
  responseSchema: UTMTableResponseDeprecatedSchema,
  querySchema: getUTMTableQuerySchema,
  snakeCaseQueryParams: true,
});

// utm options for pop-up
export const getUTMOptionsQueryOptions = defaultClient.query({
  url: `ga4-service/utm-configs/populate_utm_options/` as const,
  method: 'GET',
  responseSchema: UTMOptionsResponseSchema,
  querySchema: getUTMOptionsWithSearchQuerySchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data;
  },
});
export const getUTMOptionsDeprecated = defaultApi.declareEndpoint({
  url: `ga4-service/utm-configs/populate_utm_options/` as const,
  method: 'GET',
  responseSchema: UTMOptionsResponseSchema,
  querySchema: getUTMOptionsQuerySchema,
  snakeCaseQueryParams: true,
});

// search with valuer resp
export const getUTMOptionsWithSearch = defaultApi.declareEndpoint({
  url: `ga4-service/utm-configs/populate_utm_options/` as const,
  method: 'GET',
  responseSchema: UTMOptionsWithSearchResponseSchema,
  querySchema: getUTMOptionsWithSearchQuerySchema,
  snakeCaseQueryParams: true,
});

export const getUTMOptionsWithSearchDeprecated = defaultApi.declareEndpoint({
  url: `ga4-service/utm-configs/populate_utm_options/` as const,
  method: 'GET',
  responseSchema: UTMOptionsWithSearchResponseSchema,
  querySchema: getUTMOptionsWithSearchQuerySchema,
  snakeCaseQueryParams: true,
});

export const getUTMOptionsQueryOptionsDeprecated = (baseQuery?: BaseQuery) =>
  makeQueryOptions(
    getUTMOptionsDeprecated,
    {
      processResponse(data) {
        return data;
      },
    },
    // `BaseQuery` is the loosely-typed default from `makeQueryOptions` (Result = unknown).
    // Cast to `any` so the concrete endpoint's narrowed Result doesn't reject the spread.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see note above
    { ...baseQuery } as any,
  );

// check populated rules to show bulk dialog before rules modification
export const getHasPopulatesRules = defaultApi.declareEndpoint({
  url: `ga4-service/rules/$ruleId/check-populated-rules` as const,
  method: 'GET',
  responseSchema: hasPopulatesRulesResponseSchema,
  querySchema: getHasPopulatesRulesQuerySchema,
  snakeCaseQueryParams: true,
});

export const getDynamicFieldsDeprecated = defaultApi.declareEndpoint({
  url: `ga4-service/v2/utm-configs/dynamic_fields` as const,
  method: 'GET',
  responseSchema: getDynamicFieldsResponseSchema,
});

export const getDynamicFieldsQueryOptionsDeprecated = makeQueryOptions(
  getDynamicFieldsDeprecated,
  {
    processResponse(data) {
      return data;
    },
  },
  { staleTime: Infinity },
);
